// src/components/admin/StatCard.jsx
import React, { useMemo } from "react";
import { Card, Statistic, Skeleton } from "antd";
import { ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import "../../styles/global.css";

/**
 * Trading-style Stat Card
 *
 * Props:
 * - title: string
 * - subtitle?: string
 * - value: number | string
 * - icon?: ReactNode
 * - trend?: "up" | "down" | null
 * - changePercent?: number|null
 * - sparkData?: number[]
 * - precision?: number
 * - loading?: boolean
 */
export default function StatCard({
  title,
  subtitle = "",
  value,
  icon = null,
  trend = null,
  changePercent = null,
  sparkData = null,
  precision = 0,
  className = "",
  loading = false,
}) {
  // derive percent from sparkData if not provided and sparkData is valid
  const derivedPercent = useMemo(() => {
    if (typeof changePercent === "number") return changePercent;
    if (!Array.isArray(sparkData) || sparkData.length < 2) return null;
    const first = Number(sparkData[0]) || 0;
    const last = Number(sparkData[sparkData.length - 1]) || 0;
    if (first === 0) return null;
    return ((last - first) / Math.abs(first)) * 100;
  }, [changePercent, sparkData]);

  const pct = typeof changePercent === "number" ? changePercent : derivedPercent;
  const pctSign = typeof pct === "number" ? (pct > 0 ? "up" : pct < 0 ? "down" : "flat") : null;
  const pctLabel = typeof pct === "number" ? `${Math.abs(pct).toFixed(1)}%` : null;

  // Sparkline rendering (small inline SVG)
  const Sparkline = ({ data = [], width = 120, height = 28 }) => {
    if (!Array.isArray(data) || data.length < 2) return null;

    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const step = width / (data.length - 1);

    const points = data.map((d, i) => {
      const x = i * step;
      const y = height - ((d - min) / range) * height;
      return `${x},${y}`;
    });

    const pathD = `M ${points.join(" L ")}`;
    const lastValue = data[data.length - 1];
    const strokeColor =
      pctSign === "up" || trend === "up"
        ? "var(--color-secondary)"
        : pctSign === "down" || trend === "down"
        ? "#e74c3c"
        : "var(--color-primary)";

    return (
      <svg className="stat-sparkline" width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden>
        <path d={pathD} fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={width - step} cy={height - ((lastValue - min) / range) * height} r="2.6" fill={strokeColor} />
      </svg>
    );
  };

  // Format numeric value with thousands separators and precision
  const valueDisplay = useMemo(() => {
    if (typeof value === "number") {
      return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      });
    }
    return value;
  }, [value, precision]);

  return (
    <Card
      bordered={false}
      hoverable
      className={`stat-card trading-card light-card ${className}`}
      bodyStyle={{ padding: 14 }}
    >
      {loading ? (
        <div className="stat-card-skeleton">
          <Skeleton.Input active size="small" style={{ width: 120, marginBottom: 8 }} />
          <Skeleton.Input active size="large" style={{ width: 100 }} />
        </div>
      ) : (
        <>
          <div className="trading-card-top">
            <div className="trading-card-left">
              {icon ? <div className="trading-card-icon">{icon}</div> : null}
              <div>
                <div className="trading-card-title span">{title}</div>
                {subtitle ? <div className="trading-card-subtitle">{subtitle}</div> : null}
              </div>
            </div>

            <div className="trading-card-right">
              {pctLabel ? (
                <div
                  className={`trading-card-badge ${
                    pct > 0 ? "pct-up" : pct < 0 ? "pct-down" : "pct-flat"
                  }`}
                  title={pctLabel}
                >
                  {pct > 0 ? <ArrowUpOutlined /> : pct < 0 ? <ArrowDownOutlined /> : null}
                  <span className="pct-text">{pctLabel}</span>
                </div>
              ) : null}
            </div>
          </div>

          <div className="trading-card-body" style={{ marginTop: 8, marginBottom: 6 }}>
            <Statistic
              value={valueDisplay}
              valueStyle={{
                color: "#0b1220", // strong dark color for white background
                fontWeight: 900,
                fontSize: "clamp(36px, 5vw, 64px)", // responsive large font
                lineHeight: 1,
                letterSpacing: "-0.02em",
              }}
            />
          </div>

          <div className="trading-card-foot">
            {sparkData ? <Sparkline data={sparkData} /> : <div style={{ height: 28 }} />}
            <div className="trading-card-meta">
              {Array.isArray(sparkData) && sparkData.length ? (
                <span className="meta-text">Last: {Number(sparkData[sparkData.length - 1]).toFixed(precision)}</span>
              ) : null}
            </div>
          </div>
        </>
      )}
    </Card>
  );
}
